// Complementary Products - Splide Carousel Class

class ComplementaryProductsSplide {
  constructor() {
    // support multiple complementary carousels on the page
    this.instances = new Map(); // element -> splideInstance
    this.observeForInsertions();
    this.observeProductRecommendations();
    // try to init any existing elements immediately
    this.initAll();
  }

  init() {
    // kept for backward compatibility; prefer per-element init via initAll
    if (typeof Splide === 'undefined') {
      console.warn('Splide library is not loaded');
      return;
    }
  }
  // Initialize Splide for all matching elements currently in DOM
  initAll() {
    const elements = document.querySelectorAll('.complementary-products-splide');
    elements.forEach(el => this.initForElement(el));
  }

  // Initialize or re-initialize Splide for a given element
  initForElement(el) {
    if (!el) return;
    if (el.dataset.splideInitialized) {
      // Already initialized, skip to prevent infinite loop
      return;
    }
    // Wait for Splide library and for slides to be present (Search & Discovery loads recommendations async)
    console.debug('[Splide] initForElement called', el);
    Promise.all([
      this.waitForSplide(),
      this.waitForSlides(el, { timeout: 8000 }),
      this.waitForVisible(el, { timeout: 4000 })
    ])
      .then(() => {
        // If an instance already exists for this element, destroy to re-create a fresh one
        const existing = this.instances.get(el);
        if (existing && typeof existing.destroy === 'function') {
          try { existing.destroy(); } catch (e) { /* ignore */ }
          this.instances.delete(el);
          // Remove initialized flag so we can re-init if needed
          delete el.dataset.splideInitialized;
        }
        // Mark as initialized to prevent re-entry
        el.dataset.splideInitialized = 'true';
        console.debug('[Splide] Mounting Splide for', el);

        const options = {
          type: 'slide',
          perPage: 1,
          perMove: 1,
          pagination: false,
          arrows: false,
          gap: 0,
          padding: 0,
          drag: true,
          keyboard: true,
          lazyLoad: 'nearby',
          breakpoints: {
            768: {
              perPage: 1,
              perMove: 1,
              gap: 0,
              padding: 0,
            },
          },
        };

        const instance = new Splide(el, options);

        // Mount and store instance
        instance.mount();
        // Force a short refresh after mount to ensure measurements are correct
        setTimeout(() => {
          try { instance.refresh(); } catch (e) { /* ignore */ }
        }, 60);
        console.debug('[Splide] Mounted and refreshed', el);
        this.instances.set(el, instance);

        // Update arrow disabled state when slides change
        instance.on('mounted moved updated refresh', () => {
          const prev = el.querySelector('.complementary-arrow-prev');
          const next = el.querySelector('.complementary-arrow-next');
          if (prev) prev.disabled = instance.index === 0;
          if (next) next.disabled = instance.index === instance.length - 1;
          console.debug('[Splide] Arrow state updated', { prev, next, index: instance.index, length: instance.length });
        });

        // Attach robust manual arrow handlers in case Splide's internal binding misses them
        const attachManualArrowHandlers = () => {
          const prev = el.querySelector('.complementary-arrow-prev');
          const next = el.querySelector('.complementary-arrow-next');
          if (prev) {
            // replace to remove any previously attached handlers
            const newPrev = prev.cloneNode(true);
            prev.parentNode.replaceChild(newPrev, prev);
            newPrev.addEventListener('click', (ev) => {
              ev.preventDefault();
              try { instance.go('<'); } catch (e) { /* ignore */ }
            });
          }
          if (next) {
            const newNext = next.cloneNode(true);
            next.parentNode.replaceChild(newNext, next);
            newNext.addEventListener('click', (ev) => {
              ev.preventDefault();
              try { instance.go('>'); } catch (e) { /* ignore */ }
            });
          }
        };

        // Try attaching immediately and also after a small delay (layout might settle)
        attachManualArrowHandlers();
        setTimeout(attachManualArrowHandlers, 120);
        console.debug('[Splide] Manual arrow handlers attached', el);

      })
      .catch((err) => {
        // If slides didn't appear yet, observe the element for children and try again
        // (Search & Discovery may inject HTML into the recommendations node)
        console.warn('[Splide] Waiting for slides or Splide failed', err, el);
        this.observeElementForSlides(el);
      });
  }

  // Promise that resolves when Splide global is available
  waitForSplide(timeout = 3000) {
    return new Promise((resolve, reject) => {
      if (typeof Splide !== 'undefined') return resolve();
      let waited = 0;
      const interval = setInterval(() => {
        if (typeof Splide !== 'undefined') {
          clearInterval(interval);
          console.debug('[Splide] Splide library detected');
          return resolve();
        }
        waited += 200;
        if (waited >= timeout) {
          clearInterval(interval);
          console.warn('[Splide] Splide not available after timeout');
          return reject(new Error('Splide not available'));
        }
      }, 200);
    });
  }

  // Wait for slides (.splide__list > li) to be present in the element
  waitForSlides(el, { timeout = 3000 } = {}) {
    return new Promise((resolve, reject) => {
      try {
        const hasSlides = () => {
          const list = el.querySelector('.splide__list');
          const found = list && list.querySelectorAll('li').length > 0;
          if (found) console.debug('[Splide] Slides detected', el);
          return found;
        };

        if (hasSlides()) return resolve();

        const observer = new MutationObserver((mutations, obs) => {
          if (hasSlides()) {
            obs.disconnect();
            return resolve();
          }
        });

        observer.observe(el, { childList: true, subtree: true });

        // fallback timeout
        setTimeout(() => {
          observer.disconnect();
          if (hasSlides()) return resolve();
          console.warn('[Splide] Timeout waiting for slides', el);
          return reject(new Error('Timeout waiting for slides'));
        }, timeout);
      } catch (e) {
        reject(e);
      }
    });
  }

  // Wait until the element is visible / has non-zero layout (helps when container is hidden initially)
  waitForVisible(el, { timeout = 2000 } = {}) {
    return new Promise((resolve, reject) => {
      try {
        const isVisible = () => {
          return el.offsetParent !== null || el.offsetWidth > 0 || el.offsetHeight > 0 || window.getComputedStyle(el).display !== 'none';
        };

        if (isVisible()) return resolve();

        let waited = 0;
        const interval = setInterval(() => {
          if (isVisible()) {
            clearInterval(interval);
            return resolve();
          }
          waited += 100;
          if (waited >= timeout) {
            clearInterval(interval);
            return resolve(); // resolve anyway; we don't want to block permanently
          }
        }, 100);
      } catch (e) {
        resolve();
      }
    });
  }

  // Observe a specific element for slides and try to init when present
  observeElementForSlides(el) {
    const obs = new MutationObserver((mutations, observer) => {
      const list = el.querySelector('.splide__list');
      if (list && list.querySelectorAll('li').length > 0) {
        observer.disconnect();
        console.debug('[Splide] Slides appeared via observer, re-initializing', el);
        // try initializing again
        this.initForElement(el);
      }
    });
    obs.observe(el, { childList: true, subtree: true });
  }

  // Observe DOM for dynamically inserted complementary carousels (product-recommendations fetch)
  observeForInsertions() {
    const observer = new MutationObserver(mutations => {
      for (const m of mutations) {
        for (const node of Array.from(m.addedNodes)) {
          if (!(node instanceof HTMLElement)) continue;
          if (node.matches && node.matches('.complementary-products-splide')) {
            this.initForElement(node);
          }
          // also check descendants
          const nested = node.querySelectorAll && node.querySelectorAll('.complementary-products-splide');
          if (nested && nested.length) nested.forEach(el => this.initForElement(el));
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  // Explicitly observe product-recommendations parent for subtree changes
  observeProductRecommendations() {
    const recs = document.querySelectorAll('product-recommendations, .product-recommendations, [data-section-type*="product-recommendations"]');
    recs.forEach(node => {
      const obs = new MutationObserver((mutations, observer) => {
        const splides = node.querySelectorAll('.complementary-products-splide');
        splides.forEach(el => this.initForElement(el));
      });
      obs.observe(node, { childList: true, subtree: true });
      console.debug('[Splide] Observing product-recommendations node', node);
    });
  }

  // Destroy all instances
  destroyAll() {
    this.instances.forEach((inst, el) => {
      try { inst.destroy(); } catch (e) { }
    });
    this.instances.clear();
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', function () {
  window.complementaryProductsSplide = new ComplementaryProductsSplide();
});

// Handle Shopify theme re-initialization
document.addEventListener('shopify:section:load', function (e) {
  const sectionContainer = e.detail.target?.querySelector('.complementary-products-section');
  if (sectionContainer) {
    // Destroy old instance if exists
    if (window.complementaryProductsSplide) {
      window.complementaryProductsSplide.destroyAll();
    }
    // Create new instance
    window.complementaryProductsSplide = new ComplementaryProductsSplide();
  }
});

document.addEventListener('shopify:section:unload', function () {
  if (window.complementaryProductsSplide) {
    window.complementaryProductsSplide.destroyAll();
  }
});